/**
 * Klaviyo Popup Swap
 *
 * Active only when the `klaviyo-popups-enabled` meta tag is present
 * (settings.use_klaviyo_popups). Responsibilities:
 *   1. Open the device-appropriate Klaviyo signup form from our "Save 15%" CTAs.
 *   2. Bridge Klaviyo form view/submit events into GA4 with trigger_source.
 *   3. On submit, mark the shopper as signed up so the promo badge reflects it.
 *
 * The discount code is delivered by Klaviyo's welcome email; the theme never
 * applies a discount to the cart.
 *
 * Klaviyo's onsite.js is commonly blocked by ad/tracking blockers; CTA clicks fall
 * back to the in-house popup so the button is never dead.
 */
(function () {
  'use strict';

  if (!document.querySelector('meta[name="klaviyo-popups-enabled"]')) return;

  // --- Config (from meta tags) ------------------------------------------------
  function meta(name) {
    return document.querySelector('meta[name="' + name + '"]')?.content || '';
  }
  const DESKTOP_FORM_ID = meta('klaviyo-popup-form-id-desktop');
  const MOBILE_FORM_ID = meta('klaviyo-popup-form-id-mobile');
  // Every configured form is "ours" — used to scope which klaviyoForms events we act on.
  const FORM_IDS = [DESKTOP_FORM_ID, MOBILE_FORM_ID].filter(Boolean);

  const SIGNUP_KEY = 'promo_signup_complete'; // shared with promo-badge.js

  // If Klaviyo's onsite.js doesn't answer an openForm within this window (blocked or
  // slow), we fall back to the in-house popup. Klaviyo normally responds in <300ms.
  const KLAVIYO_OPEN_TIMEOUT = 1200;
  const MOBILE_MQ =
    (window.FoxTheme && FoxTheme.config && FoxTheme.config.mediaQueryMobile) || '(max-width: 767px)';

  // --- Safe storage (privacy-mode / WebViews may throw or return null) ---------
  function set(store, key, value) {
    try { if (store) store.setItem(key, value); } catch (_e) { /* unavailable */ }
  }

  // --- Analytics bridge (GA4 via gtag, matching subscription-popup.js) ---------
  function track(eventName, params) {
    if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
  }

  // Distinguish a shopper-initiated open (clicked a CTA) from an auto popup.
  // `lastOpenSource` is a transient flag set by open(); `currentFormSource` latches
  // it for the open form so the eventual submit is attributed to the same source.
  let lastOpenSource = null;
  let lastOpenAt = 0;
  let currentFormSource = 'auto_popup';
  function resolveOpenSource() {
    // Only trust the CTA flag if it was set in the last few seconds.
    if (lastOpenSource && Date.now() - lastOpenAt < 5000) {
      const source = lastOpenSource;
      lastOpenSource = null;
      return source;
    }
    return 'auto_popup';
  }

  // --- Form scoping & device selection ----------------------------------------
  function isOurForm(detail) {
    // Fail safe: with no configured IDs we claim NO form, so an unrelated Klaviyo
    // form (e.g. back-in-stock) is never treated as our signup form.
    if (!FORM_IDS.length) return false;
    const id = detail && (detail.formId || detail.formID || detail.id);
    return id ? FORM_IDS.indexOf(String(id)) !== -1 : false;
  }
  function selectFormId() {
    const isMobile = window.matchMedia(MOBILE_MQ).matches;
    return isMobile ? MOBILE_FORM_ID || DESKTOP_FORM_ID : DESKTOP_FORM_ID || MOBILE_FORM_ID;
  }

  // --- Adblock / load-failure fallback ---------------------------------------
  let fallbackTimer = null;
  // Depends on the Popup section (sections/popup.liquid, in overlay-group) staying
  // enabled in the theme editor. Disable it and blocked shoppers get a dead CTA
  // rather than a fallback — the `use_klaviyo_popups` setting says so too.
  function openInHousePopup() {
    const popup = document.querySelector('subscription-popup');
    if (popup && typeof popup.show === 'function') popup.show();
  }

  // --- Public open API (called by promo-badge.js on CTA click) ----------------
  window.KlaviyoPopup = {
    open: function (source) {
      lastOpenSource = source || 'cta_click';
      lastOpenAt = Date.now();
      // klaviyo-form-monitor.js attributes the next form open to this CTA.
      document.dispatchEvent(new CustomEvent('klaviyo-popup:open-requested', { detail: { source: lastOpenSource } }));

      const formId = selectFormId();
      if (!formId) {
        console.warn('[KlaviyoPopup] No Klaviyo form ID configured; opening in-house popup.');
        openInHousePopup();
        return;
      }

      const klOnsite = (window._klOnsite = window._klOnsite || []);
      klOnsite.push(['openForm', formId]);

      // If Klaviyo is blocked/slow, no klaviyoForms 'open' event fires for an
      // explicit click — degrade to the in-house popup so the CTA still works.
      if (fallbackTimer) clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(function () {
        fallbackTimer = null;
        document.dispatchEvent(new CustomEvent('klaviyo-popup:fallback', {
          detail: { reason: 'no_open', source: lastOpenSource },
        }));
        openInHousePopup();
      }, KLAVIYO_OPEN_TIMEOUT);
    },
  };

  // --- Klaviyo onsite form events --------------------------------------------
  window.addEventListener('klaviyoForms', function (e) {
    const detail = e && e.detail;
    if (!detail) return;

    // Cancel the adblock fallback on ANY Klaviyo open, before the form-scope check
    // below. The fallback exists to answer "did Klaviyo respond at all" — once a
    // Klaviyo form is on screen the answer is yes, even if it isn't the form we
    // asked for (stale form ID in settings, Klaviyo serving a variant). Scoping
    // this to our own IDs would stack the in-house popup on top of a live Klaviyo
    // form, which is the exact collision the swap is meant to remove.
    if (detail.type === 'open' && fallbackTimer) {
      clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }

    if (!isOurForm(detail)) return;

    if (detail.type === 'open') {
      currentFormSource = resolveOpenSource();
      track('popup_view', {
        popup_section: 'klaviyo',
        popup_trigger: currentFormSource, // legacy param name for existing GA4 dashboards
        popup_id: detail.formId || detail.formID || detail.id || '',
        trigger_source: currentFormSource,
      });
      return;
    }

    if (detail.type === 'submit') {
      track('popup_signup', {
        popup_section: 'klaviyo',
        popup_trigger: currentFormSource,
        popup_id: detail.formId || detail.formID || detail.id || '',
        trigger_source: currentFormSource,
      });

      // Attribute the signup source on the Klaviyo profile; email comes from the
      // form metadata. The welcome flow delivers the discount code.
      const email = detail.metaData && (detail.metaData.$email || detail.metaData.email);
      if (email) {
        const learnq = (window._learnq = window._learnq || []);
        learnq.push(['identify', {
          $email: email,
          popup_trigger_source: currentFormSource,
        }]);
      }

      // Reflect the signup in the badge. The code arrives by email, so the badge
      // points there rather than claiming anything was applied to the cart.
      set(window.localStorage, SIGNUP_KEY, 'true');
      document.querySelectorAll('promo-badge').forEach(function (badge) {
        if (typeof badge.setActivatedState === 'function') badge.setActivatedState();
      });
    }
  });
})();
